import { ArrayUtils } from "utils";

import { CROP_TYPE_FCPO, PRACTICE_SLUG, PracticeFCPO, PracticeFCPORequest, ZERO_UUID } from "models";

import {
  BULK_PRACTICE_STATE,
  PracticeConfigForBulk,
  mineralKeysToCompare,
  organicKeysToCompare,
  tillageKeysToCompare,
} from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { getIsSoilPlus3DataCollectionEditable } from "pages/app/all-field-view/utils/dataEntryUtils.ts";
import {
  type MissingDataDrawerPracticeType,
  type MissingDataForm,
  YIELD_PSEUDO_PRACTICE_TYPE,
} from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";

// data points the bulk drawer edits through the bulk crop endpoint rather than a per-practice one. Neither is a
// PRACTICE_SLUG, so they get their own pseudo types alongside YIELD
export const BULK_CROP_DATA_POINT = {
  cropName: "CROP_NAME",
  residue: "RESIDUE",
} as const;
export type BulkCropDataPointType = (typeof BULK_CROP_DATA_POINT)[keyof typeof BULK_CROP_DATA_POINT];

// every data point the drawer can offer: the per-practice ones plus the two crop-level ones
export type BulkDataPointType = MissingDataDrawerPracticeType | BulkCropDataPointType;

export const isBulkCropDataPoint = (dataPointType: BulkDataPointType): dataPointType is BulkCropDataPointType =>
  Object.values(BULK_CROP_DATA_POINT).includes(dataPointType as BulkCropDataPointType);

// "add" only fills the crops that have no value yet and never overwrites; "edit" replaces the value on every crop
// still open for editing. The two are separate entry points from the bulk action bar's Add data / Edit data buttons
export const BULK_DATA_DRAWER_MODE = {
  add: "add",
  edit: "edit",
} as const;
export type BulkDataDrawerMode = (typeof BULK_DATA_DRAWER_MODE)[keyof typeof BULK_DATA_DRAWER_MODE];

export const BULK_DATA_POINT_UNAVAILABLE_REASON = {
  verified: "verified",
  mixedCropTypes: "mixedCropTypes",
  mixedCropNames: "mixedCropNames",
  complete: "complete",
} as const;
export type BulkDataPointUnavailableReason = (typeof BULK_DATA_POINT_UNAVAILABLE_REASON)[keyof typeof BULK_DATA_POINT_UNAVAILABLE_REASON];

type BulkDataPoint = {
  practiceType: BulkDataPointType;
  iconName: string;
  // suffix of the historicalPracticeDataTableView:bulkDataDrawer.dataPoints.* keys
  labelKey: string;
};

// the data points the bulk drawer can edit, in the order the design lays the card grid out
export const BULK_DATA_POINTS: BulkDataPoint[] = [
  { practiceType: BULK_CROP_DATA_POINT.cropName, iconName: "klim/crops-matured", labelKey: "cropName" },
  { practiceType: PRACTICE_SLUG.CROPS, iconName: "tabler/calendar-event", labelKey: "growingPeriod" },
  { practiceType: YIELD_PSEUDO_PRACTICE_TYPE, iconName: "tabler/scale", labelKey: "yield" },
  { practiceType: PRACTICE_SLUG.SEEDINGS, iconName: "tabler/seeding", labelKey: "seedings" },
  { practiceType: BULK_CROP_DATA_POINT.residue, iconName: "tabler/growth", labelKey: "residue" },
  { practiceType: PRACTICE_SLUG.TILLAGES, iconName: "klim/tillages", labelKey: "tillages" },
  { practiceType: PRACTICE_SLUG.ORGANIC_FERTILIZERS, iconName: "klim/organicFertilizers", labelKey: "organicFertilizers" },
  { practiceType: PRACTICE_SLUG.MINERAL_FERTILIZERS, iconName: "klim/fertilizer", labelKey: "mineralFertilizers" },
];

// crop-specific values: a selection spanning several crop names has no single crop to pick them for
const CROP_NAME_BOUND_DATA_POINT_TYPES: BulkDataPointType[] = [BULK_CROP_DATA_POINT.cropName, PRACTICE_SLUG.CROPS, YIELD_PSEUDO_PRACTICE_TYPE];

export const UNDERSOWING_DATA_POINT_TYPES: BulkDataPointType[] = [BULK_CROP_DATA_POINT.cropName, PRACTICE_SLUG.CROPS, BULK_CROP_DATA_POINT.residue];

export const getApplicableDataPointTypes = (
  availablePracticeTypes: BulkDataPointType[],
  { cropTypes, showResidue }: { cropTypes: CROP_TYPE_FCPO[]; showResidue: boolean }
): BulkDataPointType[] => {
  const isUndersowing = cropTypes.includes(CROP_TYPE_FCPO.UNDERSOWING);
  const applicableToCropType = isUndersowing
    ? availablePracticeTypes.filter((dataPointType) => UNDERSOWING_DATA_POINT_TYPES.includes(dataPointType))
    : availablePracticeTypes;

  return showResidue ? applicableToCropType : applicableToCropType.filter((dataPointType) => dataPointType !== BULK_CROP_DATA_POINT.residue);
};

// mirrors the farmFieldCropIds filter the bulk save applies per practice type, so a card never promises more
// editable values than the save actually writes
export const IS_ITEM_EDITABLE: Partial<Record<BulkDataPointType, (item: MissingDataForm) => boolean>> = {
  // a confirmed crop's name and residue are locked, as in Timeline's crop drawer
  [BULK_CROP_DATA_POINT.cropName]: (item) => !item.crop?.isConfirmed,
  // an undersowing's biomass follows its growing period instead: both are what the farmer still fills in for a crop
  // whose data is otherwise verified
  [BULK_CROP_DATA_POINT.residue]: (item) => !item.crop?.isConfirmed || (item.crop.cropType === CROP_TYPE_FCPO.UNDERSOWING && item.isGrowingPeriodEditable),
  [PRACTICE_SLUG.CROPS]: (item) => item.isGrowingPeriodEditable,
  [YIELD_PSEUDO_PRACTICE_TYPE]: (item) => item.isYieldEditable !== false,
  [PRACTICE_SLUG.SEEDINGS]: (item) => item.isSeedingEditable,
  [PRACTICE_SLUG.TILLAGES]: (item) => item.isTillageEditable || item.isTillageDepthEditable || item.areTillageDatesEditable,
  [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: (item) => item.isOrganicEditable || item.areOrganicDatesEditable,
  [PRACTICE_SLUG.MINERAL_FERTILIZERS]: (item) => item.isMineralEditable !== false || !!item.areMineralDatesEditable,
};

// whether a crop already holds a value for a data point at all, independent of whether that value may be changed
export const HAS_ITEM_VALUE: Partial<Record<BulkDataPointType, (item: MissingDataForm) => boolean>> = {
  [BULK_CROP_DATA_POINT.cropName]: (item) => !!item.crop?.cropId,
  [BULK_CROP_DATA_POINT.residue]: (item) => item.crop?.hasResidue != null,
  [PRACTICE_SLUG.CROPS]: (item) => !!item.crop?.seedingStartDate && !!item.crop?.harvestEndDate,
  [YIELD_PSEUDO_PRACTICE_TYPE]: (item) => item.yieldPerHa != null,
  [PRACTICE_SLUG.SEEDINGS]: (item) => (item.seedings?.length ?? 0) > 0,
  [PRACTICE_SLUG.TILLAGES]: (item) => (item.tillages?.length ?? 0) > 0,
  [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: (item) => (item.organicFertilizers?.length ?? 0) > 0,
  [PRACTICE_SLUG.MINERAL_FERTILIZERS]: (item) => (item.mineralFertilizers?.length ?? 0) > 0,
};

export type BulkDataPointState = BulkDataPoint & {
  // how many of the selected crops this save would actually write to, in the drawer's current mode
  writableCount: number;
  // how many already hold a value — add mode shows this as "2/3 filled"
  filledCount: number;
  selectedCount: number;
  // only part of the selection would be written to, so the card spells the count out
  isPartial: boolean;
  unavailableReason?: BulkDataPointUnavailableReason;
};

// Multi-value data points hold a list of entries. Add mode appends a new entry on top of a crop's existing ones, so a
// crop takes it as long as none of its entries is verified — however many it already has
const CAN_ITEM_TAKE_NEW_ENTRY: Partial<Record<BulkDataPointType, (item: MissingDataForm) => boolean>> = {
  [PRACTICE_SLUG.TILLAGES]: (item) => item.isTillageEditable,
  [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: (item) => item.isOrganicEditable,
  [PRACTICE_SLUG.MINERAL_FERTILIZERS]: (item) => item.isMineralEditable !== false,
};

export const isMultiValueDataPoint = (dataPointType: BulkDataPointType): boolean => dataPointType in CAN_ITEM_TAKE_NEW_ENTRY;

// add mode skips a crop that already holds a single value but appends to a multi-value one; edit mode rewrites it
export const isItemWritable = (item: MissingDataForm, dataPointType: BulkDataPointType, mode: BulkDataDrawerMode): boolean => {
  const canTakeNewEntry = CAN_ITEM_TAKE_NEW_ENTRY[dataPointType];
  if (mode === BULK_DATA_DRAWER_MODE.add && canTakeNewEntry) return canTakeNewEntry(item);
  if (!IS_ITEM_EDITABLE[dataPointType]?.(item)) return false;
  if (mode === BULK_DATA_DRAWER_MODE.edit) return true;
  return !HAS_ITEM_VALUE[dataPointType]?.(item);
};

export const getBulkDataPointStates = (
  missingDataFormItems: MissingDataForm[],
  availablePracticeTypes: BulkDataPointType[],
  mode: BulkDataDrawerMode
): BulkDataPointState[] =>
  BULK_DATA_POINTS.filter((dataPoint) => availablePracticeTypes.includes(dataPoint.practiceType)).map((dataPoint) => {
    const selectedCount = missingDataFormItems.length;
    const writableCount = missingDataFormItems.filter((item) => isItemWritable(item, dataPoint.practiceType, mode)).length;
    const filledCount = missingDataFormItems.filter((item) => HAS_ITEM_VALUE[dataPoint.practiceType]?.(item) ?? false).length;

    // the crops on offer depend on the crop type, so a selection spanning several has no single list to pick from
    const hasMixedCropTypes = new Set(missingDataFormItems.map((item) => item.crop?.cropType)).size > 1;
    const isCropNameAmbiguous = dataPoint.practiceType === BULK_CROP_DATA_POINT.cropName && hasMixedCropTypes;
    const hasMixedCropNames =
      CROP_NAME_BOUND_DATA_POINT_TYPES.includes(dataPoint.practiceType) &&
      new Set(missingDataFormItems.map((item) => item.crop?.cropId).filter((cropId) => !!cropId)).size > 1;
    // in add mode a fully filled single-value data point has nothing left to add, and the card points at edit mode
    // instead. A multi-value one always takes another entry
    const isAlreadyComplete =
      mode === BULK_DATA_DRAWER_MODE.add && !isMultiValueDataPoint(dataPoint.practiceType) && filledCount === selectedCount && selectedCount > 0;

    return {
      ...dataPoint,
      writableCount,
      filledCount,
      selectedCount,
      isPartial: writableCount > 0 && writableCount < selectedCount,
      unavailableReason: hasMixedCropNames
        ? BULK_DATA_POINT_UNAVAILABLE_REASON.mixedCropNames
        : isCropNameAmbiguous
        ? BULK_DATA_POINT_UNAVAILABLE_REASON.mixedCropTypes
        : isAlreadyComplete
        ? BULK_DATA_POINT_UNAVAILABLE_REASON.complete
        : writableCount === 0
        ? BULK_DATA_POINT_UNAVAILABLE_REASON.verified
        : undefined,
    };
  });

// the drawer opens on the first data point it can actually write to, so the bulk action bar doesn't have to pick one
export const getFirstEditableDataPoint = (dataPointStates: BulkDataPointState[]): BulkDataPointType | undefined =>
  dataPointStates.find((dataPointState) => !dataPointState.unavailableReason)?.practiceType;

export type BulkDataPointStatus = "filled" | "missing" | "locked";

export type BulkFieldBreakdownChip = {
  practiceType: BulkDataPointType;
  labelKey: string;
  status: BulkDataPointStatus;
  // entries beyond the first, rendered as "Tillage (+3)" — only multi-entry practices ever report one
  additionalEntryCount: number;
};

export type BulkFieldBreakdownRow = {
  fieldId: string;
  cropId: string;
  // the crop chip shows the crop itself rather than the words "crop name", so the row carries both the name and the
  // slug the practices namespace is keyed by
  cropName: string;
  cropTranslationSlug: string;
  chips: BulkFieldBreakdownChip[];
  // the values behind the chips, for their tooltips. Naming a practice needs the practices list and a translation,
  // so the row hands the crop over and the component reads what each chip needs off it
  missingDataFormItem: MissingDataForm;
};

// per-crop status of every offered data point, for the drawer header's "Show details" breakdown. A locked chip wins
// over a filled one: the farmer's question there is what this save can still touch
// a data point the save can't write to is still worth opening when the crops hold something: the design asks for
// verified values to be readable ("give possibility to look at entry(s) that are verified"), just not editable.
// The other reasons leave nothing to look at, so those cards stay dead
export const isBulkDataPointSelectable = (dataPointState: BulkDataPointState): boolean =>
  !dataPointState.unavailableReason || (dataPointState.unavailableReason === BULK_DATA_POINT_UNAVAILABLE_REASON.verified && dataPointState.filledCount > 0);

export const getBulkFieldBreakdown = (missingDataFormItems: MissingDataForm[], availablePracticeTypes: BulkDataPointType[]): BulkFieldBreakdownRow[] =>
  missingDataFormItems.map((item) => ({
    fieldId: item.fieldId,
    cropId: item.key,
    cropName: item.crop?.cropName ?? "",
    cropTranslationSlug: item.crop?.translationSlug ?? "",
    missingDataFormItem: item,
    chips: BULK_DATA_POINTS.filter((dataPoint) => availablePracticeTypes.includes(dataPoint.practiceType)).map((dataPoint) => {
      const hasValue = HAS_ITEM_VALUE[dataPoint.practiceType]?.(item) ?? false;
      const isEditable = IS_ITEM_EDITABLE[dataPoint.practiceType]?.(item) ?? false;
      const entryCount = getEntryCount(item, dataPoint.practiceType);

      return {
        practiceType: dataPoint.practiceType,
        labelKey: dataPoint.labelKey,
        status: !isEditable ? "locked" : hasValue ? "filled" : "missing",
        additionalEntryCount: Math.max(entryCount - 1, 0),
      };
    }),
  }));

const getEntryCount = (item: MissingDataForm, practiceType: BulkDataPointType): number => getItemPractices(item, practiceType).length;

// the entries a crop holds for a multi-value data point; empty for every other data point
export const getItemPractices = (item: MissingDataForm, practiceType: BulkDataPointType): PracticeFCPO[] => {
  if (practiceType === PRACTICE_SLUG.TILLAGES) return item.tillages ?? [];
  if (practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS) return item.organicFertilizers ?? [];
  if (practiceType === PRACTICE_SLUG.MINERAL_FERTILIZERS) return item.mineralFertilizers ?? [];
  return [];
};

export type BulkEntryAppliedTo = {
  // the selected crops that already carry this exact practice entry
  fieldIds: string[];
  appliedCount: number;
  selectedCount: number;
};

export const getEntryAppliedTo = (
  missingDataFormItems: MissingDataForm[],
  practiceType: BulkDataPointType,
  entry: PracticeFCPO
): BulkEntryAppliedTo | undefined => {
  if (!entry.assignedPracticeId) return undefined;

  const matchingItems = missingDataFormItems.filter((item) =>
    getItemPractices(item, practiceType).some((practice) => isSameEntry(practiceType, entry, practice))
  );
  // an entry the farmer is adding right now exists on no crop yet — the tag would read "applied to 0/6", so leave it off
  if (matchingItems.length === 0) return undefined;

  return {
    fieldIds: Array.from(new Set(matchingItems.map((item) => item.fieldId))),
    appliedCount: matchingItems.length,
    selectedCount: missingDataFormItems.length,
  };
};

// Fields whose existing value an edit-mode save would replace: the crop must both already hold a value and still be
// open for editing. Crops that are empty gain data rather than lose it, and verified ones are skipped entirely — so
// neither belongs in the "you are about to overwrite" count.
export const getOverwrittenFieldIds = (missingDataFormItems: MissingDataForm[], dataPointType: BulkDataPointType): string[] => {
  const overwrittenItems = missingDataFormItems.filter(
    (item) => (HAS_ITEM_VALUE[dataPointType]?.(item) ?? false) && isItemWritable(item, dataPointType, BULK_DATA_DRAWER_MODE.edit)
  );

  return Array.from(new Set(overwrittenItems.map((item) => item.fieldId)));
};

export const getEntryPendingApplication = (
  missingDataFormItems: MissingDataForm[],
  dataPointType: BulkDataPointType,
  mode: BulkDataDrawerMode = BULK_DATA_DRAWER_MODE.add
): BulkEntryAppliedTo | undefined => {
  const pendingItems = missingDataFormItems.filter((item) => isItemWritable(item, dataPointType, mode));
  if (pendingItems.length === 0) return undefined;

  return {
    fieldIds: Array.from(new Set(pendingItems.map((item) => item.fieldId))),
    appliedCount: pendingItems.length,
    selectedCount: missingDataFormItems.length,
  };
};

const KEYS_TO_COMPARE: Partial<Record<BulkDataPointType, (keyof PracticeFCPO)[]>> = {
  [PRACTICE_SLUG.TILLAGES]: tillageKeysToCompare,
  [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: organicKeysToCompare,
  [PRACTICE_SLUG.MINERAL_FERTILIZERS]: mineralKeysToCompare,
};

const isSameEntry = (practiceType: BulkDataPointType, entry: PracticeFCPO, candidate: PracticeFCPO): boolean =>
  (KEYS_TO_COMPARE[practiceType] ?? []).every((key) => (entry[key] ?? null) === (candidate[key] ?? null));

// A stored entry as the practice endpoints take it back. They ignore what ties it to its own crop row, and leaving that
// in would keep otherwise identical lists apart
export const toPracticeRequest = ({ id: _id, cropId: _cropId, year: _year, fallbackDates: _fallbackDates, ...entry }: PracticeFCPO): PracticeFCPORequest =>
  entry as PracticeFCPORequest;

const withoutZeroEntries = (practices: PracticeFCPO[]) => practices.filter((practice) => practice.assignedPracticeId !== ZERO_UUID);

export type SharedEntryGroup = {
  entry: PracticeFCPO;
  rowsByCropKey: Record<string, PracticeFCPO>;
};

export const getSharedEntryGroups = (
  missingDataFormItems: MissingDataForm[],
  practiceType: BulkDataPointType,
  mode: BulkDataDrawerMode
): SharedEntryGroup[] => {
  const writableItems = missingDataFormItems.filter((item) => isItemWritable(item, practiceType, mode));
  if (mode === BULK_DATA_DRAWER_MODE.add && writableItems.length > 0) return [];

  const entriesByCrop = (writableItems.length > 0 ? writableItems : missingDataFormItems)
    .map((item) => ({ cropKey: item.key, entries: withoutZeroEntries(getItemPractices(item, practiceType)) }))
    .filter(({ entries }) => entries.length > 0);
  const isTypeShared = (assignedPracticeId: string) =>
    entriesByCrop.every(({ entries }) => entries.some((entry) => entry.assignedPracticeId === assignedPracticeId));

  return entriesByCrop
    .flatMap(({ cropKey, entries }) => entries.filter((entry) => isTypeShared(entry.assignedPracticeId)).map((entry) => ({ cropKey, entry })))
    .reduce<SharedEntryGroup[]>((groups, { cropKey, entry }) => {
      const identicalGroupIndex = groups.findIndex((group) => !(cropKey in group.rowsByCropKey) && isSameEntry(practiceType, group.entry, entry));
      if (identicalGroupIndex === -1) return [...groups, { entry, rowsByCropKey: { [cropKey]: entry } }];

      return groups.map((group, index) => (index === identicalGroupIndex ? { ...group, rowsByCropKey: { ...group.rowsByCropKey, [cropKey]: entry } } : group));
    }, [])
    .sort(
      (first, second) =>
        first.entry.assignedPracticeId.localeCompare(second.entry.assignedPracticeId) ||
        (first.entry.startDate ?? "").localeCompare(second.entry.startDate ?? "")
    );
};

export const getSharedEntries = (missingDataFormItems: MissingDataForm[], practiceType: BulkDataPointType, mode: BulkDataDrawerMode): PracticeFCPO[] =>
  getSharedEntryGroups(missingDataFormItems, practiceType, mode).map((group) => group.entry);

// What the practice form opens with: the shared entries, answered "yes" when there are any and "no" when every crop
// intentionally has none. Replaces getPracticeConfigForBulk here, which locks the form as soon as the crops differ
export const getBulkPracticeConfig = (
  missingDataFormItems: MissingDataForm[],
  practiceType: BulkDataPointType,
  mode: BulkDataDrawerMode,
  isReadOnly: boolean,
  lastSubmissionYear: number | null | undefined
): PracticeConfigForBulk => {
  const sharedEntries = getSharedEntries(missingDataFormItems, practiceType, mode);
  const writableEntryLists = missingDataFormItems
    .filter((item) => isItemWritable(item, practiceType, mode))
    .map((item) => getItemPractices(item, practiceType));
  const isEveryCropAnsweredNo =
    mode === BULK_DATA_DRAWER_MODE.edit &&
    writableEntryLists.length > 0 &&
    writableEntryLists.every((entries) => entries.length > 0 && withoutZeroEntries(entries).length === 0);
  const areAllCropsSame = writableEntryLists.every((entries) =>
    ArrayUtils.arraysEqualByKeys(entries, writableEntryLists[0], KEYS_TO_COMPARE[practiceType] ?? [])
  );

  const bulkPracticeState: BULK_PRACTICE_STATE =
    mode === BULK_DATA_DRAWER_MODE.edit && areAllCropsSame && writableEntryLists.length > 0 ? "ALL_SAME" : "NO_PRACTICES";

  const hasConfirmedEntries = writableEntryLists.some((entries) =>
    entries.some((entry) => entry.isConfirmed && !getIsSoilPlus3DataCollectionEditable(entry.soilPlus3DataCollection, lastSubmissionYear))
  );

  return {
    practiceToShow: sharedEntries,
    showMessage: false,
    toggleState: sharedEntries.length > 0 ? true : isEveryCropAnsweredNo ? false : null,
    toggleDisabled: isReadOnly || hasConfirmedEntries,
    bulkPracticeState,
  };
};
